import { computed } from "vue";

/**
 * Opciones de Google Charts para los gráficos del panel de evolución.
 * Separado de useEvolution para mantener SRP.
 *
 * @param {import('vue').Ref<object|null>} evolutionDataRef — ref con los datos raw de evolución
 * @param {import('vue').ComputedRef<boolean>} isMultiSeasonRef — computed que indica multi-temporada
 * @returns {{
 *     revenueAttendanceOptions: import('vue').ComputedRef<object>,
 *     accumulatedChartOptions: import('vue').ComputedRef<object>,
 *     positionRevenueOptions: import('vue').ComputedRef<object>,
 *     multiPositionOptions: import('vue').ComputedRef<object>,
 * }}
 */
export function useEvolutionCharts(evolutionDataRef, isMultiSeasonRef) {
    /** Revenue + Attendance options (single: dual Y-axis; multi: dual Y-axis with alternating dashed attendance per season) */
    const revenueAttendanceOptions = computed(() => {
        if (isMultiSeasonRef.value) {
            const perSeason = evolutionDataRef.value?.data?.per_season || {};
            const entries = Object.entries(perSeason);
            const series = {};
            for (let i = 0; i < entries.length; i++) {
                series[i * 2] = { targetAxisIndex: 0 };
                series[i * 2 + 1] = { targetAxisIndex: 1, lineDashStyle: [4, 4] };
            }
            return {
                series,
                vAxes: {
                    0: { title: "Ingresos", format: '#,##0€' },
                    1: { title: "Asistencia", format: "short" },
                },
                hAxis: { title: "Jornada" },
                curveType: "function",
                legend: { position: "top" },
                chartArea: { width: "85%", height: "70%" },
            };
        }
        return {
            series: {
                0: { targetAxisIndex: 0 },
                1: { targetAxisIndex: 1 },
            },
            vAxes: {
                0: { title: "Ingresos", format: '#,##0€' },
                1: { title: "Asistencia", format: "short" },
            },
            hAxis: { title: "Jornada" },
            colors: ["#e30613", "#6b7280"],
            curveType: "function",
            legend: { position: "top" },
            chartArea: { width: "85%", height: "70%" },
        };
    });

    /** Accumulated bar chart options */
    const accumulatedChartOptions = computed(() => {
        if (isMultiSeasonRef.value) {
            return {
                legend: { position: "top" },
                hAxis: { title: "Jornada" },
                vAxis: { format: '#,##0€' },
                chartArea: { width: "75%", height: "70%" },
                bar: { groupWidth: '65%' },
            };
        }
        return {
            colors: ["#e30613"],
            legend: { position: "none" },
            hAxis: { title: "Jornada" },
            vAxis: { format: '#,##0€' },
            chartArea: { width: "75%", height: "70%" },
            bar: { groupWidth: '65%' },
            isStacked: true,
        };
    });

    /** Position (line, inverted) + Revenue (bar) combined chart */
    const positionRevenueOptions = computed(() => ({
        seriesType: "line",
        series: {
            0: { type: "line", targetAxisIndex: 0 },
            1: { type: "bars", targetAxisIndex: 1 },
        },
        vAxes: {
            0: { title: "Posición", direction: -1, minValue: 0 },
            1: { title: "Ingresos", format: '#,##0€' },
        },
        hAxis: { title: "Jornada" },
        colors: ["#2563eb", "#e30613"],
        legend: { position: "none" },
        chartArea: { width: "75%", height: "70%" },
    }));

    /** Multi-season position line chart options */
    const multiPositionOptions = computed(() => ({
        curveType: "function",
        legend: { position: "top" },
        hAxis: { title: "Jornada" },
        chartArea: { width: "75%", height: "70%" },
    }));

    return {
        revenueAttendanceOptions,
        accumulatedChartOptions,
        positionRevenueOptions,
        multiPositionOptions,
    };
}
